import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ArrowUpRight, Calendar, MessageCircle, BookOpen, Sparkles, Store } from "lucide-react";

function StatTile({ label, value, sub }) {
  return (
    <div className="bg-white border border-[var(--lh-border)] p-7" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="overline text-[var(--lh-mauve)] mb-3">{label}</div>
      <div className="font-serif-display text-5xl tracking-tighter leading-none">{value}</div>
      {sub && <div className="overline text-[var(--lh-muted)] mt-2">{sub}</div>}
    </div>
  );
}

export default function DashHome() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [business, setBusiness] = useState(null);

  useEffect(() => {
    api.get("/events").then(({ data }) => setEvents(data));
    api.get("/businesses/me").then(({ data }) => setBusiness(data && data.business_id ? data : null)).catch(() => setBusiness(null));
  }, []);

  // profile completion score
  const fields = ["name", "category", "city", "description", "website", "instagram", "photo_url"];
  const filled = business ? fields.filter((f) => business[f]).length : 0;
  const completion = business ? Math.round((filled / fields.length) * 100) : 0;

  return (
    <div className="space-y-12" data-testid="dashboard-home">
      <div>
        <div className="overline text-[var(--lh-mauve)] mb-3">Welcome back</div>
        <h1 className="font-serif-display text-5xl md:text-6xl tracking-tighter leading-[0.95]">
          Hi {user?.name?.split(" ")[0] || "there"}.<br /><em>Let's go win the week.</em>
        </h1>
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        <StatTile label="Profile" value={`${completion}%`} sub={business ? "Keep going" : "Start your listing"} />
        <StatTile label="Tier" value={user?.tier ? user.tier.charAt(0).toUpperCase() + user.tier.slice(1) : "Free"} sub={user?.tier ? "Member benefits unlocked" : "Choose a plan"} />
        <StatTile label="Upcoming events" value={events.length} sub="This season" />
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 bg-white border border-[var(--lh-border)] p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="overline text-[var(--lh-mauve)] mb-2">Upcoming events</div>
              <h2 className="font-serif-display text-3xl">Show up. Be seen.</h2>
            </div>
            <Link to="/dashboard/events" className="overline text-[var(--lh-black)] hover:text-[var(--lh-mauve)]">All events →</Link>
          </div>
          <ul className="space-y-3">
            {events.slice(0, 4).map((e) => {
              const d = new Date(e.date);
              return (
                <li key={e.event_id} className="py-5 flex items-center gap-5">
                  <div className="text-center w-14 shrink-0">
                    <div className="font-serif-display text-3xl leading-none">{d.getDate()}</div>
                    <div className="overline text-[var(--lh-mauve)] mt-1">{d.toLocaleString("en", { month: "short" })}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-serif-display text-xl truncate">{e.title}</div>
                    <div className="overline text-[var(--lh-muted)] mt-1">{e.city} · {e.time}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="lg:col-span-5 space-y-6">
          <div className="bg-[var(--lh-black)] text-white p-8" data-testid="dashboard-business-cta">
            <div className="overline text-[var(--lh-mauve)] mb-3">My Business</div>
            <div className="font-serif-display text-3xl leading-tight">
              {business ? "Polish your listing." : "Claim your spot in the HER Guide."}
            </div>
            <p className="mt-3 text-white/75 text-sm font-light">
              {business ? `${completion}% complete — finish your profile to boost visibility.` : "Get discovered by locals searching women-owned businesses."}
            </p>
            <Link to="/dashboard/business" className="btn-mauve mt-6">
              {business ? "Edit listing" : "Create listing"} <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className="bg-white border border-[var(--lh-border)] p-7">
            <div className="overline text-[var(--lh-mauve)] mb-3">Recommended</div>
            <ul className="space-y-4">
              <li className="flex items-start gap-3"><BookOpen size={18} className="mt-1 text-[var(--lh-mauve)]" /><div><div className="font-medium">Local Marketing Playbook</div><div className="overline text-[var(--lh-muted)]">24 pages · download</div></div></li>
              <li className="flex items-start gap-3"><Sparkles size={18} className="mt-1 text-[var(--lh-mauve)]" /><div><div className="font-medium">AI prompts for founders</div><div className="overline text-[var(--lh-muted)]">30 prompts</div></div></li>
              <li className="flex items-start gap-3"><MessageCircle size={18} className="mt-1 text-[var(--lh-mauve)]" /><div><div className="font-medium">Jump into #brand-clinic</div><div className="overline text-[var(--lh-muted)]">9 active today</div></div></li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
